import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import type { EventView } from '../../events/labels';
import { DialogLayer } from '../../ui/Dialog';
import { offerUrl } from './offers';
import { OFFER_STATE_LABELS, OFFERS_KEY, type OfferListRow, type OfferOf } from './types';

/**
 * „Dodaj do oferty łączonej…” in an event's quote (owner 2026-10-30): the client's offers — a draft takes the event,
 * an offer that has it already is opened — or „Nowa oferta łączona” with this event.
 */
export function AddToOfferDialog({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const event = useQuery({ queryKey: ['events', 'detail', eventId, 'offer'], queryFn: () => api<EventView>(`/api/events/${eventId}`) });
  const clientId = event.data?.contractorId ?? null;
  const offers = useQuery({ queryKey: [...OFFERS_KEY, 'client', clientId], enabled: !!clientId, queryFn: () => api<{ offers: OfferListRow[] }>(`/api/quotes/offers?clientId=${clientId}`) });
  const done = (id: string) => {
    void queryClient.invalidateQueries({ queryKey: OFFERS_KEY });
    navigate(offerUrl(id));
  };
  const add = useMutation({
    mutationFn: async (offerId: string) => {
      const of = await api<OfferOf>(`/api/quotes/offers/${offerId}`);
      await api(`/api/quotes/offer-versions/${of.current.id}`, { method: 'PATCH', body: { eventIds: [...of.current.events.map((e) => e.eventId), eventId], version: of.current.version } });
      return offerId;
    },
    onSuccess: done,
  });
  const create = useMutation({ mutationFn: () => api<OfferOf>('/api/quotes/offers', { method: 'POST', body: { clientId, eventIds: [eventId] } }), onSuccess: (of) => done(of.offer.id) });
  const rows = offers.data?.offers ?? [];
  const failed = add.error ?? create.error ?? event.error ?? offers.error;
  const busy = add.isPending || create.isPending;
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="add-offer-title" data-feature-id="quotes.offers">
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="add-offer-title">Dodaj do oferty łączonej</h2>
        {event.data && !clientId && <p className="alert">To wydarzenie nie ma klienta. Oferta łączona jest dla jednego klienta — najpierw ustaw go w szczegółach wydarzenia.</p>}
        {clientId && offers.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
        {clientId && !offers.isLoading && rows.length === 0 && <p className="muted">Ten klient nie ma jeszcze ofert łączonych.</p>}
        {rows.length > 0 && (
          <ul className="offer-choice">
            {rows.map((r) => {
              const has = r.eventIds.includes(eventId);
              return (
                <li key={r.id}>
                  <span>
                    <strong>{r.name || `Oferta dla: ${r.clientName}`}</strong> <span className="muted">v{r.number} {OFFER_STATE_LABELS[r.state]}, wydarzeń: {r.eventCount}</span>
                  </span>
                  {has ? (
                    <button className="btn small" type="button" onClick={() => done(r.id)}>
                      Otwórz (już jest)
                    </button>
                  ) : r.state === 'robocza' ? (
                    <button className="btn small" type="button" disabled={busy} onClick={() => add.mutate(r.id)}>
                      Dodaj
                    </button>
                  ) : (
                    <span className="hint">wysłana — dodasz w nowej wersji</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {failed && (
          <p className="alert" role="alert">
            {failed instanceof ApiError ? failed.message : 'Nie udało się'}
          </p>
        )}
        <div className="actions">
          {clientId && (
            <button className="btn primary" type="button" disabled={busy} onClick={() => create.mutate()}>
              Nowa oferta łączona
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
